/**
 * BulkActionsBar Component
 *
 * Sticky action bar that appears when transactions are selected.
 * Provides bulk actions like classification and selection management.
 *
 * @see types/accounting.ts for Transaction type
 */

import { useCallback } from 'react';
import clsx from 'clsx';
import { CheckSquare, Tag, X, CheckCircle } from 'lucide-react';

export interface BulkActionsBarProps {
  /** Number of currently selected transactions */
  selectedCount: number;
  /** Total number of transactions in the view */
  totalCount: number;
  /** Callback when "Classify" is clicked (opens bulk classification modal) */
  onClassify: () => void;
  /** Callback to clear all selections */
  onClearSelection: () => void;
  /** Callback to select all visible transactions */
  onSelectAll: () => void;
  /** Whether a bulk operation is currently in progress */
  isLoading?: boolean;
}

/**
 * BulkActionsBar renders a sticky action bar at the bottom of the screen.
 *
 * Features:
 * - Shows selected count
 * - Select All button to select all visible transactions
 * - Classify button to open bulk classification
 * - Clear button to deselect all
 * - Loading state during bulk operations
 * - Sticky positioning at bottom of viewport
 *
 * The bar is only visible when at least one transaction is selected.
 *
 * @example
 * ```tsx
 * {selectedIds.size > 0 && (
 *   <BulkActionsBar
 *     selectedCount={selectedIds.size}
 *     totalCount={transactions.length}
 *     onClassify={() => setShowClassifyModal(true)}
 *     onClearSelection={() => setSelectedIds(new Set())}
 *     onSelectAll={() => setSelectedIds(new Set(transactions.map(t => t.id)))}
 *     isLoading={isBulkClassifying}
 *   />
 * )}
 * ```
 */
export function BulkActionsBar({
  selectedCount,
  totalCount,
  onClassify,
  onClearSelection,
  onSelectAll,
  isLoading = false,
}: BulkActionsBarProps) {
  const allSelected = selectedCount === totalCount && totalCount > 0;

  const handleSelectAll = useCallback(() => {
    if (!isLoading) {
      onSelectAll();
    }
  }, [onSelectAll, isLoading]);

  const handleClassify = useCallback(() => {
    if (!isLoading && selectedCount > 0) {
      onClassify();
    }
  }, [onClassify, isLoading, selectedCount]);

  const handleClear = useCallback(() => {
    if (!isLoading) {
      onClearSelection();
    }
  }, [onClearSelection, isLoading]);

  // Don't render if nothing is selected
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div
      className={clsx(
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-white border-t border-gray-200 shadow-lg',
        'animate-in slide-in-from-bottom-4 duration-200'
      )}
      role="toolbar"
      aria-label="Bulk actions"
      data-testid="bulk-actions-bar"
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          {/* Selection info */}
          <div className="flex items-center gap-2">
            <CheckSquare
              className="w-5 h-5 text-blue-600"
              aria-hidden="true"
            />
            <span className="text-sm font-medium text-gray-900">
              {selectedCount} selected
            </span>
            {!allSelected && (
              <span className="text-sm text-gray-500">
                of {totalCount}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Select All button (if not all selected) */}
            {!allSelected && (
              <button
                type="button"
                onClick={handleSelectAll}
                disabled={isLoading}
                className={clsx(
                  'flex items-center gap-2 px-3 py-1.5 text-sm',
                  'border rounded-md',
                  'transition-colors duration-150',
                  isLoading
                    ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500'
                )}
                data-testid="select-all-button"
              >
                <CheckCircle className="w-4 h-4" aria-hidden="true" />
                Select All ({totalCount})
              </button>
            )}

            {/* Classify button */}
            <button
              type="button"
              onClick={handleClassify}
              disabled={isLoading || selectedCount === 0}
              className={clsx(
                'flex items-center gap-2 px-4 py-1.5 text-sm font-medium',
                'rounded-md',
                'transition-colors duration-150',
                isLoading || selectedCount === 0
                  ? 'bg-blue-300 text-white cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              )}
              data-testid="bulk-classify-button"
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Classifying...
                </>
              ) : (
                <>
                  <Tag className="w-4 h-4" aria-hidden="true" />
                  Classify
                </>
              )}
            </button>

            {/* Clear selection button */}
            <button
              type="button"
              onClick={handleClear}
              disabled={isLoading}
              className={clsx(
                'flex items-center gap-1 p-1.5',
                'rounded-md',
                'transition-colors duration-150',
                isLoading
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                'focus:outline-none focus:ring-2 focus:ring-blue-500'
              )}
              aria-label="Clear selection"
              data-testid="clear-selection-button"
            >
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BulkActionsBar;
